import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { getEventsLegends, getLocationValidateItemCounter } from "api/layer";
import FathomIcon from "components/fathomIcon/fathomIcon";
import Svg from "components/svg/svg";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { handleFilterLocations } from "helpers/helpers";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./fathom-popup.module.scss";
import {
  buildClimateRiskScores,
  CRS_RESPONSE_STRUCTURE,
  filterFathomLayers,
  filterNonFathomLayers
} from "../shared/analysis-shared";
import {
  executeMultipleAnalysisWithReporting,
  syncMultipleAnalysisRunningStatus,
  MultipleAnalysisParams
} from "../shared/multiple-analysis-executor";

export default function MultipleFathomPopup() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();

  const [charging, setCharging] = useState(0);
  const [usedPrcentage, setUsedPrcentage] = useState(0);//0 未满80% 1 已满80% 2 超出限额
  const [donotAskAgain, setDonotAskAgain] = useState(false);
  const { AMap, map } = useContext(MapContext);
  const locationsTable = handleFilterLocations({
    data: context.currentMultipleLocations?.items || [],
    unit: context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
    multipleLocationsSearch: context.multipleLocationsSearch,
    locationsFilterInfo: context.locationsFilterInfo,
    filterChecked: true
  });

  const onClose = () => {
    dispatch({ type: AppAction.setMultipleFathomPopup, payload: undefined });
  };

  const selectedButtonArray = context.selectLayer;

  /** 多点 Fathom 确认弹窗，逻辑与 single-fathom-popup 保持一致 */
  const runAnalysis = async (selectedButtonsParam: any[]) => {
    dispatch({ type: AppAction.setAskAgain, payload: donotAskAgain });

    const selectedButtons = selectedButtonsParam?.length ? selectedButtonsParam : selectedButtonArray;
    const selectedEvent = context.selectedEvent;
    if (!selectedButtons?.length) {
      return;
    }
    if (context.rightPanel === RightPanel.Multiple || context.rightPanel === RightPanel.Events) {
      if (
        context.currentMultipleLocationsAnalysed &&
        context.currentMultipleLocations &&
        context.currentMultipleLocationsAnalysed.id !== context.currentMultipleLocations.id
      ) {
        dispatch({ type: AppAction.setHazardMultipleAnalysedLegends, payload: undefined });
        dispatch({ type: AppAction.setHazardMultipleAnalysisStore, payload: undefined });
        dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: undefined });
        dispatch({ type: AppAction.setClimateMultipleAnalysedLegends, payload: undefined });
        dispatch({ type: AppAction.setClimateMultipleAnalysisResult, payload: undefined });
        dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: undefined });
        dispatch({ type: AppAction.setEventAnalysisLegend, payload: undefined });
        dispatch({ type: AppAction.setEventAnalysisStore, payload: undefined });
        dispatch({ type: AppAction.setEventAnalysisStatus, payload: undefined });
      }
      context.rightPanel === RightPanel.Events && dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple })
      let params: MultipleAnalysisParams = {
        eventLayers: [],
        catNetLayers: [],
        responseStructure: [],
        climateRiskScores: [],
        locations: []
      };
      if (selectedButtons[0].length) {
        params.catNetLayers = selectedButtons[0];
        dispatch({
          type: AppAction.setHazardMultipleAnalysedLegends,
          payload: context.hazardLegends?.filter((item) => {
            return selectedButtons[0].includes(item.layerId);
          })
        });
      }

      if (selectedButtons[1].length) {
        params.climateRiskScores = buildClimateRiskScores(selectedButtons[1]);
        params.responseStructure = [CRS_RESPONSE_STRUCTURE];
        dispatch({
          type: AppAction.setClimateMultipleAnalysedLegends,
          payload: context.crsLayerList?.filter((item) => {
            return selectedButtons[1].includes(item.id);
          })
        });
      }

      if (selectedEvent?.id && selectedEvent?.layerId) {
        params.eventLayers = [selectedEvent.layerId];
        dispatch({
          type: AppAction.setSelectedEvent,
          payload: selectedEvent,
        });

        if (context.eventsList?.length && !selectedEvent.checked) {
          selectedEvent.checked = true;
          if (selectedEvent.layerObj) {
            selectedEvent.layerObj.show()
            let findLengend = context.eventsLegend?.find((e: any) => e.layerInfo.layerId === selectedEvent.layerId);
            dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...findLengend, layerInfo: selectedEvent }] })
            dispatch({ type: AppAction.updateEventsLegend, payload: selectedEvent });
          } else {
            const layerObj = new AMap.TileLayer({
              tileUrl: `${(window as any).amapLayerUrl}/risklayer-api/catnet/tiles/${selectedEvent.layerId}/tile/[z]/[x]/[y]?X-Token=${sessionStorage.getItem("token")}`,
              opacity: selectedEvent.opacity,
            });
            selectedEvent.layerObj = layerObj;
            map.addLayer(layerObj);
            getEventsLegends({ layers: selectedEvent.layerId }).then(async (res: any) => {
              if (res.legends?.length) {
                dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...res.legends[0], layerInfo: selectedEvent }] })
                dispatch({ type: AppAction.addEventsLegend, payload: { ...res.legends[0], layerInfo: selectedEvent } })
              }
            });
          }
          const updatedEvent = context.eventsList.map(e => {
            if (e.layerId === selectedEvent.layerId) {
              return { ...e, checked: true }
            }
            return e;
          })
          dispatch({ type: AppAction.setSelectedEventsList, payload: [...context.selectedEventsList, selectedEvent] });
          dispatch({ type: AppAction.setEventsList, payload: updatedEvent });
        } else {
          let findLengend = context.eventsLegend?.find((e: any) => e.layerInfo.layerId === selectedEvent.layerId);
          dispatch({ type: AppAction.setEventAnalysisLegend, payload: [{ ...findLengend, layerInfo: selectedEvent }] })
        }
      }

      locationsTable.forEach((item) => {
        params.locations!.push({
          id: item.id,
          coordinates: {
            latitude: item.latitudeWgs,
            longitude: item.longitudeWgs
          }
        });
      });

      if (context.currentMultipleLocationsAnalysed?.id !== context.currentMultipleLocations?.id) {
        dispatch({ type: AppAction.setHazardMultipleAnalysisStore, payload: undefined });
        dispatch({ type: AppAction.setClimateMultipleAnalysisResult, payload: undefined });
        dispatch({ type: AppAction.setEventAnalysisStore, payload: undefined });
      } else {
        params.catNetLayers = params.catNetLayers?.filter((item) => {
          let layerMap = context.hazardMultipleAnalysisStore?.get(item);
          if (layerMap) {
            let analysisResultNum = params.locations?.filter((location) => layerMap?.get(location.id.split("-").join(""))).length;
            return analysisResultNum != params.locations?.length
          } else {
            return true;
          }
        });

        params.climateRiskScores = params.climateRiskScores?.filter((item) => {
          if (context.climateMultipleAnalysisResult?.[0]?.climateRiskScores) {
            let analysisResultNum = context.climateMultipleAnalysisResult?.filter((result) => {
              if (params.locations?.find((location) => location.id.split("-").join("") === result.id.split("-").join(""))) {
                return result.climateRiskScores.find((layers) => layers.index === item.index)
              } else {
                return false
              }
            }).length
            return analysisResultNum != params.locations?.length
          } else {
            return true;
          }
        });

        params.eventLayers = params.eventLayers?.filter((item) => {
          let layerMap = context.eventAnalysisStore?.get(item);
          if (layerMap) {
            let analysisResultNum = params.locations?.filter((location) => layerMap?.get(location.id.split("-").join(""))).length;
            return analysisResultNum != params.locations?.length
          } else {
            return true;
          }
        });
      }

      syncMultipleAnalysisRunningStatus(params, dispatch);

      onClose();
      dispatch({ type: AppAction.setCurrentMultipleLocationsAnalysed, payload: { ...context.currentMultipleLocations } });
      dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
      dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: true });
      executeMultipleAnalysisWithReporting(params, dispatch);
    }
  };

  const handelNo = () => {
    dispatch({ type: AppAction.setAskAgain, payload: donotAskAgain });

    if (!selectedButtonArray || !selectedButtonArray.length) {
      return;
    }

    const hasOtherElements = filterNonFathomLayers(selectedButtonArray[0]).length > 0;

    if (hasOtherElements) {
      const selectedButtonParam = filterNonFathomLayers(selectedButtonArray[0]);

      dispatch({
        type: AppAction.setSelectLayer, payload: [
          selectedButtonParam,
          selectedButtonArray[1]
        ]
      });

      runAnalysis([
        selectedButtonParam,
        selectedButtonArray[1]
      ])
    } else {
      dispatch({ type: AppAction.setMultipleAnalyseModal, payload: { type: "2" } });
      onClose();
    }

  }

  useEffect(() => {
    if (context.multipleFathomPopup) {
      if (!selectedButtonArray || !selectedButtonArray.length) {
        return;
      }
      let params: any = {
        eventLayers: [],
        catNetLayers: [],
        responseStructure: [],
        climateRiskScores: []
      };
      if (selectedButtonArray[0].length) {
        params.catNetLayers = selectedButtonArray[0];
      }

      if (selectedButtonArray[1].length) {
        params.climateRiskScores = selectedButtonArray[1].map((item: any) => {
          return { index: item };
        });
        params.responseStructure = [CRS_RESPONSE_STRUCTURE];
      }

      if (context.selectedEvent) {
        params.eventLayers = [context.selectedEvent.layerId];
      }

      locationsTable.forEach((item) => {
        if (!params.locations) {
          params.locations = []
        }
        params.locations!.push({
          id: item.id,
          coordinates: {
            latitude: item.latitudeWgs,
            longitude: item.longitudeWgs
          }
        });
      });
      getLocationValidateItemCounter(params).then(res => {
        if (res.data.itemFathomQuotas && res.data.itemFathomQuotas > 0) {
          if ((res.data.trItemFathomCount + (res.data.notExistFathomCount ?? 0)) > res.data.itemFathomQuotas) {
            setUsedPrcentage(2)
          } else if ((res.data.trItemFathomCount / res.data.itemFathomQuotas) >= 0.8) {
            setUsedPrcentage(1)
          } else {
            setUsedPrcentage(0)
          }
        }
        setCharging(res.data.notExistFathomCount ?? 0)
      })
    }
  }, [context.multipleFathomPopup])

  return (
    <Dialog maxWidth="xs" open={!!context.multipleFathomPopup && context.rightPanel !== RightPanel.Single} onClose={onClose}>
      <DialogTitle className={styles.title}>
        {t("fathom.popup.title")}
      </DialogTitle>

      <DialogContent>
        <div className={styles.content}>
          <div>{t("fathom.popup.text")}</div>
          <div>{t("fathom.popup.charging", { num: charging })}</div>
          {usedPrcentage === 1 && <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.warning") }}></span>}
          {usedPrcentage === 2 && <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.error") }}></span>}
        </div>

        {selectedButtonArray?.[0]?.length &&
          filterFathomLayers(selectedButtonArray[0])
            .map((e: string) => {
              return (
                <div key={e} className={styles.item}>
                  <div>{Svg({ icon: e })()}</div>
                  <div style={{ marginLeft: "6px" }}>{t("layerId." + e)}</div>
                  <FathomIcon className={styles.premium} />
                </div>
              );
            })}

        <div>
          <Checkbox color="success"
            onChange={() => setDonotAskAgain(!donotAskAgain)}
            checked={donotAskAgain}
          />
          {t("fathom.popup.ask")}
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          color="success"
          variant="outlined"
          onClick={() => handelNo()}
          style={{ margin: "0 10px 10px 0" }}
        >
          {t("buttons.no")}
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={() => runAnalysis([])}
          style={{ margin: "0 10px 10px 0" }}
          disabled={usedPrcentage === 2}
        >
          {t("buttons.yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


